import { ArtistFollow, useIdentitySubmissionStatus } from "./MeetingFeatures";
import type { Artist, Circle, Post, User } from "@kira/contracts";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Bookmark,
  BookOpenText,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  FileBadge,
  FileCheck2,
  FileHeart,
  FileKey2,
  FileLock2,
  FileText,
  GripVertical,
  Heart,
  IdCard,
  LockKeyhole,
  MailQuestion,
  MessageCircleMore,
  Phone,
  Radio,
  ReceiptText,
  Settings2,
  Share2,
  ShieldCheck,
  Sparkles,
  Star,
  TicketCheck,
  UserRoundCheck,
  UserRoundPlus,
  UsersRound,
  X,
  Zap,
} from "lucide-react";
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import "./fan-prd-experience.css";

const compactNumber = new Intl.NumberFormat("zh-CN", {
  notation: "compact",
  maximumFractionDigits: 1,
});
const postDate = new Intl.DateTimeFormat("zh-CN", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

function initials(value: string): string {
  const normalized = value.trim();
  return normalized ? Array.from(normalized).slice(0, 2).join("").toUpperCase() : "K";
}

function ExperienceAvatar({ src, name, size = "medium" }: {
  src?: string;
  name: string;
  size?: "small" | "medium" | "large";
}) {
  return (
    <span className={`kpx-avatar kpx-avatar--${size}`} aria-label={name}>
      <span aria-hidden="true">{initials(name)}</span>
      {src ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          onError={(event) => { event.currentTarget.hidden = true; }}
        />
      ) : null}
    </span>
  );
}

function ExperienceHeading({ eyebrow, title, description, trailing, titleId }: {
  eyebrow: string;
  title: string;
  description?: string;
  trailing?: ReactNode;
  titleId?: string;
}) {
  return (
    <header className="kpx-heading">
      <div>
        <span>{eyebrow}</span>
        <h2 id={titleId}>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {trailing ? <div className="kpx-heading__trailing">{trailing}</div> : null}
    </header>
  );
}

function ExperienceEmpty({ icon, title, description, action }: {
  icon: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="kpx-empty" role="status">
      <span className="kpx-empty__stamp" aria-hidden="true">{icon}</span>
      <small>暂无内容</small>
      <strong>{title}</strong>
      <p>{description}</p>
      {action}
    </div>
  );
}

export type ArtistDirectoryFilter = "popular" | "celebrity" | "influencer" | "subscribed";
export type ArtistDirectoryKind = "celebrity" | "influencer";

export interface ArtistDirectoryProps {
  artists: readonly Artist[];
  filter: ArtistDirectoryFilter;
  onFilterChange: (filter: ArtistDirectoryFilter) => void;
  onArtistOpen: (artist: Artist) => void;
  getArtistKind?: (artist: Artist) => ArtistDirectoryKind;
  title?: string;
  description?: string;
}

const artistFilters: ReadonlyArray<{ value: ArtistDirectoryFilter; label: string; code: string }> = [
  { value: "popular", label: "热门", code: "HOT" },
  { value: "celebrity", label: "明星", code: "STAR" },
  { value: "subscribed", label: "已订阅", code: "订阅中" },
];

function defaultArtistKind(artist: Artist): ArtistDirectoryKind {
  return /红人|达人|博主|主播|creator|influencer|virtual/i.test(artist.category)
    ? "influencer"
    : "celebrity";
}

export function ArtistDirectory({
  artists,
  filter,
  onFilterChange,
  onArtistOpen,
  getArtistKind = defaultArtistKind,
  title = "全部艺人",
  description = "从舞台热度到订阅通行，找到下一位想长期见证的人。",
}: ArtistDirectoryProps) {
  const titleId = useId();
  const list = useMemo(() => {
    const filtered = artists.filter((artist) => {
      if (getArtistKind(artist) !== "celebrity") return false;
      if (filter === "subscribed") return artist.isSubscribed;
      if (filter === "celebrity" || filter === "influencer") return getArtistKind(artist) === filter;
      return true;
    });
    return [...filtered].sort((left, right) => right.heat - left.heat || left.stageName.localeCompare(right.stageName, "zh-CN"));
  }, [artists, filter, getArtistKind]);

  return (
    <section className="kpx-directory kpx-artist-directory" aria-labelledby={titleId}>
      <ExperienceHeading
        eyebrow="发现艺人"
        title={title}
        titleId={titleId}
        description={description}
        trailing={<span className="kpx-count-pass"><b>{String(list.length).padStart(2, "0")}</b> 位在场</span>}
      />
      <div className="kpx-segmented" role="group" aria-label="筛选艺人">
        {artistFilters.map((item) => (
          <button
            type="button"
            key={item.value}
            aria-pressed={filter === item.value}
            onClick={() => onFilterChange(item.value)}
          >
            <small>{item.code}</small>
            <span>{item.label}</span>
          </button>
        ))}
      </div>

      <div className="kpx-artist-grid" aria-live="polite">
        {list.map((artist, index) => (
          <article className="kpx-artist-card" key={artist.id} style={{ "--kpx-order": index } as CSSProperties}>
            <button type="button" onClick={() => onArtistOpen(artist)} aria-label={`打开 ${artist.stageName} 的艺人主页`}>
              <span className="kpx-artist-card__visual">
                {artist.coverUrl || artist.avatarUrl ? (
                  <img
                    src={artist.coverUrl || artist.avatarUrl}
                    alt=""
                    loading="lazy"
                    onError={(event) => { event.currentTarget.hidden = true; }}
                  />
                ) : <span className="kpx-artist-card__initials">{initials(artist.stageName)}</span>}
                <span className="kpx-artist-card__wash" />
                <span className="kpx-artist-card__serial">NO. {String(index + 1).padStart(2, "0")}</span>
                <span className={`kpx-artist-card__pass${artist.isSubscribed ? " is-active" : ""}`}>
                  {artist.isSubscribed ? <><Check size={13} />通行中</> : <>待订阅</>}
                </span>
              </span>
              <span className="kpx-artist-card__body">
                <span className="kpx-artist-card__identity">
                  <small>{artist.groupName || artist.category}</small>
                  <strong>{artist.stageName}</strong>
                </span>
                <span className="kpx-artist-card__metrics">
                  <span><Zap size={14} />{compactNumber.format(artist.heat)}</span>
                  <span className={artist.heatDelta >= 0 ? "is-up" : "is-down"}>
                    {artist.heatDelta >= 0 ? "+" : ""}{artist.heatDelta}
                  </span>
                </span>
                <span className="kpx-artist-card__footer">
                  <span><Radio size={14} />{compactNumber.format(artist.subscriberCount)} 人订阅</span>
                  <ChevronRight size={17} />
                </span>
              </span>
            </button>
            <ArtistFollow artist={artist}/>
          </article>
        ))}
      </div>
      {list.length === 0 ? (
        <ExperienceEmpty
          icon={<TicketCheck size={24} />}
          title={filter === "subscribed" ? "还没有订阅艺人" : "这个分区暂时没有艺人"}
          description={filter === "subscribed" ? "从热门艺人开始看看，订阅后会在这里集中出现。" : "换一个筛选条件，新的舞台可能正在隔壁亮灯。"}
          action={filter !== "popular" ? <button className="kpx-text-action" type="button" onClick={() => onFilterChange("popular")}>查看热门艺人<ArrowRight size={15} /></button> : undefined}
        />
      ) : null}
    </section>
  );
}

export type CircleDirectorySort = "popular" | "members" | "activity" | "topPostLikes" | "alphabetical";

export interface CircleDirectoryMetric {
  hotScore?: number;
  topPostLikes?: number;
  pinyin?: string;
}

export interface CircleDirectoryProps {
  circles: readonly Circle[];
  sort: CircleDirectorySort;
  onSortChange: (sort: CircleDirectorySort) => void;
  onCircleOpen: (circle: Circle) => void;
  metrics?: Readonly<Record<string, CircleDirectoryMetric | undefined>>;
  onCommunityMembershipToggle?: (circle: Circle) => void;
  title?: string;
}

const circleSorts: ReadonlyArray<{ value: CircleDirectorySort; label: string }> = [
  { value: "popular", label: "热门" },
  { value: "members", label: "成员数" },
  { value: "activity", label: "活跃度" },
  { value: "topPostLikes", label: "热帖点赞" },
  { value: "alphabetical", label: "拼音" },
];

function circleMetric(metrics: CircleDirectoryProps["metrics"], circleId: string): CircleDirectoryMetric {
  return metrics?.[circleId] ?? {};
}

export function CircleDirectory({
  circles,
  sort,
  onSortChange,
  onCircleOpen,
  metrics,
  onCommunityMembershipToggle,
  title = "全部圈子",
}: CircleDirectoryProps) {
  const titleId = useId();
  const list = useMemo(() => {
    const next = [...circles].filter((circle) => circle.status === "published");
    next.sort((left, right) => {
      const leftMetric = circleMetric(metrics, left.id);
      const rightMetric = circleMetric(metrics, right.id);
      if (sort === "members") return right.memberCount - left.memberCount || right.activeScore - left.activeScore;
      if (sort === "activity") return right.activeScore - left.activeScore || right.memberCount - left.memberCount;
      if (sort === "topPostLikes") return (rightMetric.topPostLikes ?? 0) - (leftMetric.topPostLikes ?? 0) || right.activeScore - left.activeScore;
      if (sort === "alphabetical") {
        return (leftMetric.pinyin ?? left.name).localeCompare(rightMetric.pinyin ?? right.name, "zh-CN", { sensitivity: "base" });
      }
      const leftHot = leftMetric.hotScore ?? left.activeScore + (leftMetric.topPostLikes ?? 0) * 2 + Math.log10(left.memberCount + 1) * 10;
      const rightHot = rightMetric.hotScore ?? right.activeScore + (rightMetric.topPostLikes ?? 0) * 2 + Math.log10(right.memberCount + 1) * 10;
      return rightHot - leftHot || right.activeScore - left.activeScore;
    });
    return next;
  }, [circles, metrics, sort]);

  return (
    <section className="kpx-directory kpx-circle-directory" aria-labelledby={titleId}>
      <ExperienceHeading
        eyebrow="发现圈子"
        title={title}
        titleId={titleId}
        description="官方圈子与同好自建圈子都在这里，排序不会改变你的关注关系。"
        trailing={<span className="kpx-count-pass"><b>{String(list.length).padStart(2, "0")}</b> 个圈子</span>}
      />
      <div className="kpx-sort-rail" role="group" aria-label="圈子排序方式">
        {circleSorts.map((item) => (
          <button
            type="button"
            key={item.value}
            aria-pressed={sort === item.value}
            onClick={() => onSortChange(item.value)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="kpx-circle-list" aria-live="polite">
        {list.map((circle, index) => {
          const extra = circleMetric(metrics, circle.id);
          return (
            <article className="kpx-circle-card" key={circle.id}>
              <button className="kpx-circle-card__open" type="button" onClick={() => onCircleOpen(circle)}>
                <span className="kpx-circle-card__cover">
                  {circle.coverUrl ? <img src={circle.coverUrl} alt="" loading="lazy" onError={(event) => { event.currentTarget.hidden = true; }} /> : <span>{initials(circle.name)}</span>}
                  <i>{String(index + 1).padStart(2, "0")}</i>
                </span>
                <span className="kpx-circle-card__content">
                  <span className="kpx-circle-card__eyebrow">
                    <small>{circle.kind === "official" ? "官方圈子" : "粉丝创建"}</small>
                    {circle.isFollowed ? <b><Check size={12} />我的圈子</b> : null}
                  </span>
                  <strong>{circle.name}</strong>
                  <p>{circle.description}</p>
                  <span className="kpx-circle-card__stats">
                    <span><UsersRound size={14} />{compactNumber.format(circle.memberCount)}</span>
                    <span><Zap size={14} />活跃 {circle.activeScore}</span>
                    {extra.topPostLikes !== undefined ? <span><Heart size={14} />热帖 {compactNumber.format(extra.topPostLikes)}</span> : null}
                  </span>
                </span>
                <ChevronRight className="kpx-circle-card__chevron" size={19} />
              </button>
              <div className="kpx-circle-card__action">
                {circle.kind === "official" ? (
                  <span className={circle.isFollowed ? "is-joined" : ""}>
                    <TicketCheck size={15} />{circle.isFollowed ? "订阅已加入" : "订阅后加入"}
                  </span>
                ) : (
                  <button
                    type="button"
                    disabled={!onCommunityMembershipToggle}
                    onClick={() => onCommunityMembershipToggle?.(circle)}
                    aria-label={`${circle.isFollowed ? "取消关注" : "关注"}${circle.name}`}
                  >
                    {circle.isFollowed ? <><Check size={15} />已关注</> : <><UsersRound size={15} />关注</>}
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>
      {list.length === 0 ? (
        <ExperienceEmpty
          icon={<UsersRound size={24} />}
          title="还没有开放的圈子"
          description="审核通过的圈子会在这里出现，当前排序状态不会影响数据。"
        />
      ) : null}
    </section>
  );
}

export interface CircleArrangeItem {
  circle: Circle;
  hidden: boolean;
}

export type CircleArrangeChange =
  | { reason: "move"; circleId: string; from: number; to: number }
  | { reason: "visibility"; circleId: string; hidden: boolean };

export interface CircleArrangeSheetProps {
  open: boolean;
  items: readonly CircleArrangeItem[];
  onChange: (items: CircleArrangeItem[], change: CircleArrangeChange) => void;
  onDone: (items: readonly CircleArrangeItem[]) => void;
  onClose: () => void;
  title?: string;
}

function visibleIndices(items: readonly CircleArrangeItem[]): number[] {
  const indices: number[] = [];
  items.forEach((item, index) => {
    if (!item.hidden) indices.push(index);
  });
  return indices;
}

function reorderVisibleItems(
  items: readonly CircleArrangeItem[],
  sourceId: string,
  targetId: string,
): { items: CircleArrangeItem[]; from: number; to: number } | null {
  const slots = visibleIndices(items);
  const visible = slots.map((index) => items[index]).filter((item): item is CircleArrangeItem => Boolean(item));
  const from = visible.findIndex((item) => item.circle.id === sourceId);
  const to = visible.findIndex((item) => item.circle.id === targetId);
  if (from < 0 || to < 0 || from === to) return null;
  const reordered = [...visible];
  const [moved] = reordered.splice(from, 1);
  if (!moved) return null;
  reordered.splice(to, 0, moved);
  const next = items.map((item) => ({ ...item }));
  slots.forEach((slot, index) => {
    const replacement = reordered[index];
    if (replacement) next[slot] = replacement;
  });
  return { items: next, from, to };
}

export function CircleArrangeSheet({
  open,
  items,
  onChange,
  onDone,
  onClose,
  title = "调整我的圈子",
}: CircleArrangeSheetProps) {
  const titleId = useId();
  const helpId = useId();
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const draggingRef = useRef<string | null>(null);
  const overRef = useRef<string | null>(null);
  const dialogRef = useRef<HTMLElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const visible = items.filter((item) => !item.hidden);
  const hidden = items.filter((item) => item.hidden);

  useEffect(() => {
    if (!open) return undefined;
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusFrame = window.requestAnimationFrame(() => {
      dialogRef.current?.querySelector<HTMLElement>("[data-kpx-dialog-close]")?.focus();
    });
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
      )).filter((element) => !element.hidden && element.getAttribute("aria-hidden") !== "true");
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) {
        event.preventDefault();
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener("keydown", onKeyDown);
      returnFocusRef.current?.focus();
      returnFocusRef.current = null;
    };
  }, [onClose, open]);

  if (!open) return null;

  function move(circleId: string, offset: -1 | 1) {
    const from = visible.findIndex((item) => item.circle.id === circleId);
    const target = visible[from + offset];
    if (from < 0 || !target) return;
    const result = reorderVisibleItems(items, circleId, target.circle.id);
    if (!result) return;
    onChange(result.items, { reason: "move", circleId, from: result.from, to: result.to });
    setAnnouncement(`${visible[from]?.circle.name ?? "圈子"}已向${offset < 0 ? "左" : "右"}移动一位`);
  }

  function toggleVisibility(circleId: string) {
    const index = items.findIndex((item) => item.circle.id === circleId);
    const current = items[index];
    if (!current) return;
    const nextHidden = !current.hidden;
    const next = items.map((item, itemIndex) => itemIndex === index ? { ...item, hidden: nextHidden } : { ...item });
    onChange(next, { reason: "visibility", circleId, hidden: nextHidden });
    setAnnouncement(`${current.circle.name}已${nextHidden ? "隐藏" : "恢复显示"}，原位置已保留`);
  }

  function clearDrag() {
    draggingRef.current = null;
    overRef.current = null;
    setDraggingId(null);
    setOverId(null);
  }

  function dropOn(targetId: string, sourceId = draggingRef.current ?? draggingId) {
    if (!sourceId) return;
    const result = reorderVisibleItems(items, sourceId, targetId);
    const source = visible.find((item) => item.circle.id === sourceId);
    if (result) {
      onChange(result.items, { reason: "move", circleId: sourceId, from: result.from, to: result.to });
      setAnnouncement(`${source?.circle.name ?? "圈子"}已移动到第 ${result.to + 1} 位`);
    }
    clearDrag();
  }

  function handleRowKeyDown(event: KeyboardEvent<HTMLLIElement>, circleId: string) {
    if (!event.altKey || (event.key !== "ArrowLeft" && event.key !== "ArrowRight")) return;
    event.preventDefault();
    move(circleId, event.key === "ArrowLeft" ? -1 : 1);
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLSpanElement>, circleId: string) {
    event.currentTarget.setPointerCapture(event.pointerId);
    draggingRef.current = circleId;
    setDraggingId(circleId);
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLSpanElement>) {
    if (!draggingRef.current) return;
    const target = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>("[data-kpx-arrange-id]");
    const targetId = target?.dataset.kpxArrangeId ?? null;
    if (!targetId || targetId === overRef.current) return;
    overRef.current = targetId;
    setOverId(targetId);
  }

  function handlePointerEnd(event: ReactPointerEvent<HTMLSpanElement>) {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    const sourceId = draggingRef.current;
    const targetId = overRef.current;
    if (sourceId && targetId) dropOn(targetId, sourceId);
    else clearDrag();
  }

  return (
    <div className="kpx-arrange-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <aside ref={dialogRef} className="kpx-arrange-sheet" role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={helpId}>
        <span className="kpx-arrange-sheet__handle" aria-hidden="true" />
        <header className="kpx-arrange-sheet__header">
          <div>
            <span>我的圈子</span>
            <h2 id={titleId}>{title}</h2>
          </div>
          <button type="button" data-kpx-dialog-close onClick={onClose} aria-label="关闭圈子排序"><X size={20} /></button>
        </header>
        <p className="kpx-arrange-sheet__help" id={helpId}>
          拖动票根或使用左右按钮调整顺序；隐藏项不会占据前台位置，但会留在原来的编排槽位。
        </p>

        <div className="kpx-arrange-sheet__section-head">
          <strong>当前展示</strong><span>{visible.length} 个</span>
        </div>
        <ol className="kpx-arrange-list">
          {visible.map((item, index) => (
            <li
              key={item.circle.id}
              data-kpx-arrange-id={item.circle.id}
              data-dragging={draggingId === item.circle.id || undefined}
              data-drag-over={overId === item.circle.id || undefined}
              onKeyDown={(event) => handleRowKeyDown(event, item.circle.id)}
              tabIndex={0}
              aria-label={`${item.circle.name}，当前第 ${index + 1} 位。按住 Alt 加左右方向键可移动。`}
            >
              <span
                className="kpx-arrange-list__grip"
                title="拖动排序"
                onPointerDown={(event) => handlePointerDown(event, item.circle.id)}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerEnd}
                onPointerCancel={clearDrag}
              ><GripVertical size={19} /></span>
              <ExperienceAvatar src={item.circle.coverUrl} name={item.circle.name} size="small" />
              <span className="kpx-arrange-list__identity"><small>{String(index + 1).padStart(2, "0")}</small><strong>{item.circle.name}</strong></span>
              <span className="kpx-arrange-list__controls">
                <button type="button" onClick={() => move(item.circle.id, -1)} disabled={index === 0} aria-label={`将${item.circle.name}向左移动`}><ChevronLeft size={18} /></button>
                <button type="button" onClick={() => move(item.circle.id, 1)} disabled={index === visible.length - 1} aria-label={`将${item.circle.name}向右移动`}><ChevronRight size={18} /></button>
                <button className="kpx-arrange-list__hide" type="button" onClick={() => toggleVisibility(item.circle.id)} aria-label={`隐藏${item.circle.name}`}>隐藏</button>
              </span>
            </li>
          ))}
        </ol>
        {visible.length === 0 ? <p className="kpx-arrange-sheet__blank">当前没有展示中的圈子，可从下方恢复。</p> : null}

        {hidden.length ? (
          <>
            <div className="kpx-arrange-sheet__section-head kpx-arrange-sheet__section-head--hidden">
              <strong>已隐藏 · 保留原位置</strong><span>{hidden.length} 个</span>
            </div>
            <ul className="kpx-hidden-list">
              {hidden.map((item) => (
                <li key={item.circle.id}>
                  <ExperienceAvatar src={item.circle.coverUrl} name={item.circle.name} size="small" />
                  <span><strong>{item.circle.name}</strong><small>恢复后回到保留槽位</small></span>
                  <button type="button" onClick={() => toggleVisibility(item.circle.id)}>恢复显示</button>
                </li>
              ))}
            </ul>
          </>
        ) : null}

        <footer className="kpx-arrange-sheet__footer">
          <span><ShieldCheck size={15} />同步当前账号的圈子编排</span>
          <button type="button" onClick={() => onDone(items)}>完成调整<Check size={17} /></button>
        </footer>
        <p className="kpx-sr-only" aria-live="polite">{announcement}</p>
      </aside>
    </div>
  );
}

export type ProfileContentTab = "posts" | "liked" | "saved";

export interface ProfileEmptyAction {
  label: string;
  onClick: () => void;
}

export interface ProfileContentTabsProps {
  tab: ProfileContentTab;
  onTabChange: (tab: ProfileContentTab) => void;
  posts: readonly Post[];
  likedPosts: readonly Post[];
  savedPosts: readonly Post[];
  onPostOpen?: (post: Post) => void;
  renderPost?: (post: Post) => ReactNode;
  emptyActions?: Partial<Record<ProfileContentTab, ProfileEmptyAction>>;
}

const profileTabs: ReadonlyArray<{ value: ProfileContentTab; label: string }> = [
  { value: "posts", label: "帖子" },
  { value: "liked", label: "赞过" },
  { value: "saved", label: "收藏" },
];

const profileEmptyCopy: Record<ProfileContentTab, { title: string; description: string }> = {
  posts: { title: "还没有留下舞台记录", description: "在圈子发布的帖子会自动沉淀到这里，不需要重复发布。" },
  liked: { title: "还没有点亮喜欢", description: "赞过的帖子会集中保存在这里，方便再次回到那个瞬间。" },
  saved: { title: "收藏夹还是空的", description: "遇到想反复看的攻略、现场或应援记录时，可以先收藏起来。" },
};

function ProfilePostPreview({ post, onOpen }: { post: Post; onOpen?: (post: Post) => void }) {
  const statusLabel = post.status === "draft"
    ? "草稿"
    : post.status === "pending"
      ? "审核中"
      : post.status === "hidden"
        ? "已隐藏"
        : post.status === "rejected" ? "未通过" : null;
  const body = (
    <>
      <header className="kpx-post-preview__header">
        <ExperienceAvatar src={post.authorAvatarUrl} name={post.authorName} size="small" />
        <span><strong>{post.authorName}</strong><small>{post.circleName} · {postDate.format(new Date(post.createdAt))}</small></span>
        {statusLabel ? <b>{statusLabel}</b> : null}
      </header>
      {post.type === "campaign" ? <span className="kpx-post-preview__campaign"><Sparkles size={14} />应援企划 · {post.campaign?.activityName ?? "联合行动"}</span> : null}
      <p className="kpx-post-preview__content">{post.content}</p>
      {post.mediaUrls.length ? (
        <span className={`kpx-post-preview__media kpx-post-preview__media--${Math.min(2, post.mediaUrls.length)}`}>
          {post.mediaUrls.slice(0, 2).map((url, index) => <img src={url} alt={`帖子图片 ${index + 1}`} loading="lazy" key={url} onError={(event) => { event.currentTarget.hidden = true; }} />)}
          {post.mediaUrls.length > 2 ? <b>+{post.mediaUrls.length - 2}</b> : null}
        </span>
      ) : null}
      <footer className="kpx-post-preview__stats">
        <span className={post.isLiked ? "is-liked" : ""}><Heart size={16} fill={post.isLiked ? "currentColor" : "none"} />{post.likeCount}</span>
        <span><MessageCircleMore size={16} />{post.commentCount}</span>
        <span className={post.isSaved ? "is-saved" : ""}><Bookmark size={15} fill={post.isSaved ? "currentColor" : "none"} />{post.isSaved ? "已收藏" : "收藏"}</span>
        <span><Share2 size={15} />分享</span>
      </footer>
    </>
  );

  const interactive = onOpen ? {
    role: "button" as const,
    tabIndex: 0,
    "aria-label": `打开${post.authorName}发布在${post.circleName}的帖子`,
    onClick: () => onOpen(post),
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      onOpen(post);
    },
  } : {};

  return <article className="kpx-post-preview" {...interactive}>{body}</article>;
}

export function ProfileContentTabs({
  tab,
  onTabChange,
  posts,
  likedPosts,
  savedPosts,
  onPostOpen,
  renderPost,
  emptyActions,
}: ProfileContentTabsProps) {
  const baseId = useId();
  const collections: Record<ProfileContentTab, readonly Post[]> = { posts, liked: likedPosts, saved: savedPosts };
  const selected = collections[tab];
  const empty = profileEmptyCopy[tab];
  const emptyAction = emptyActions?.[tab];

  function handleTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, current: ProfileContentTab) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const index = profileTabs.findIndex((item) => item.value === current);
    const offset = event.key === "ArrowLeft" ? -1 : 1;
    const next = profileTabs[(index + offset + profileTabs.length) % profileTabs.length];
    if (next) {
      onTabChange(next.value);
      document.getElementById(`${baseId}-tab-${next.value}`)?.focus();
    }
  }

  return (
    <section className="kpx-profile-content" aria-labelledby={`${baseId}-title`}>
      <ExperienceHeading
        eyebrow="我的动态"
        title="我的动态"
        titleId={`${baseId}-title`}
        description="查看你发布、点赞和收藏的内容。"
        trailing={<span className="kpx-count-pass"><b>{String(selected.length).padStart(2, "0")}</b> 条记录</span>}
      />
      <div className="kpx-profile-tabs" role="tablist" aria-label="个人内容分类">
        {profileTabs.map((item) => (
          <button
            type="button"
            role="tab"
            id={`${baseId}-tab-${item.value}`}
            aria-selected={tab === item.value}
            aria-controls={`${baseId}-panel-${item.value}`}
            tabIndex={tab === item.value ? 0 : -1}
            key={item.value}
            onClick={() => onTabChange(item.value)}
            onKeyDown={(event) => handleTabKeyDown(event, item.value)}
          >
            <span>{item.label}</span><b>{collections[item.value].length}</b>
          </button>
        ))}
      </div>
      <div
        className="kpx-profile-panel"
        role="tabpanel"
        id={`${baseId}-panel-${tab}`}
        aria-labelledby={`${baseId}-tab-${tab}`}
        tabIndex={0}
      >
        {selected.map((post) => renderPost
          ? <div className="kpx-profile-panel__custom" key={post.id}>{renderPost(post)}</div>
          : <ProfilePostPreview post={post} {...(onPostOpen ? { onOpen: onPostOpen } : {})} key={post.id} />)}
        {selected.length === 0 ? (
          <ExperienceEmpty
            icon={tab === "posts" ? <Radio size={24} /> : tab === "liked" ? <Heart size={24} /> : <Bookmark size={24} />}
            title={empty.title}
            description={empty.description}
            action={emptyAction ? <button className="kpx-text-action" type="button" onClick={emptyAction.onClick}>{emptyAction.label}<ArrowRight size={15} /></button> : undefined}
          />
        ) : null}
      </div>
    </section>
  );
}

export type AccountHubAction =
  | "realname"
  | "phone"
  | "subscriptions"
  | "orders"
  | "following"
  | "followers"
  | "settings"
  | "help"
  | "about"
  | "logout";

export interface AccountHubCounts {
  subscriptions?: number;
  orders?: number;
  following?: number;
  followers?: number;
}

export interface AccountHubProps {
  open?: boolean;
  user: User;
  counts?: AccountHubCounts;
  onAction: (action: AccountHubAction) => void;
  onClose?: () => void;
  authenticationMode?: "safe-demo" | "connected";
  showLogout?: boolean;
}

interface AccountMenuItem {
  action: AccountHubAction;
  label: string;
  description: string;
  countKey?: keyof AccountHubCounts;
}

const accountSections: ReadonlyArray<{ label: string; items: readonly AccountMenuItem[] }> = [
  {
    label: "身份与安全",
    items: [
      { action: "realname", label: "实名认证", description: "提交资料（演示，不实际核验）" },
      { action: "phone", label: "手机号绑定", description: "提交手机号（演示，不发送验证码）" },
    ],
  },
  {
    label: "我的关系",
    items: [
      { action: "subscriptions", label: "订阅艺人", description: "管理艺人订阅权益", countKey: "subscriptions" },
      { action: "orders", label: "我的订单", description: "交易与物流记录", countKey: "orders" },
      { action: "following", label: "我的关注", description: "关注的明星与加入的圈子", countKey: "following" },
    ],
  },
  {
    label: "服务与规则",
    items: [
      { action: "settings", label: "设置", description: "通知、隐私与账号偏好" },
      { action: "help", label: "帮助与反馈", description: "问题反馈与服务支持" },
      { action: "about", label: "关于 KIRA", description: "版本、协议与联系我们" },
    ],
  },
];

function accountIcon(action: AccountHubAction): ReactNode {
  if (action === "realname") return <IdCard size={20} />;
  if (action === "phone") return <Phone size={20} />;
  if (action === "subscriptions") return <TicketCheck size={20} />;
  if (action === "orders") return <ReceiptText size={20} />;
  if (action === "following") return <UserRoundCheck size={20} />;
  if (action === "followers") return <UserRoundPlus size={20} />;
  if (action === "settings") return <Settings2 size={20} />;
  if (action === "help") return <CircleHelp size={20} />;
  if (action === "about") return <BookOpenText size={20} />;
  return <ArrowLeft size={20} />;
}

export function AccountHub({
  open = true,
  user,
  counts,
  onAction,
  onClose,
  authenticationMode: _authenticationMode = "safe-demo",
  showLogout = true,
}: AccountHubProps) {
  const titleId = useId();
  const identity = useIdentitySubmissionStatus(user);
  if (!open) return null;
  return (
    <aside className="kpx-account-hub" aria-labelledby={titleId}>
      <header className="kpx-account-hub__header">
        <span>账号与设置</span>
        {onClose ? <button type="button" onClick={onClose} aria-label="关闭账号中心"><X size={20} /></button> : null}
      </header>
      <section className="kpx-account-profile">
        <ExperienceAvatar src={user.avatarUrl} name={user.displayName} size="large" />
        <div>
          <h2 id={titleId}>{user.displayName}</h2>
          <p>{user.bio || "管理你的资料、关注与订单"}</p>
        </div>
      </section>

      <nav className="kpx-account-menu" aria-label="账号功能">
        {accountSections.map((section) => (
          <section key={section.label}>
            <h3>{section.label}</h3>
            <div>
              {section.items.map((item) => {
                const verification = item.action === "realname"
                  ? Boolean(identity?.realname)
                  : item.action === "phone" ? Boolean(identity?.phone) : null;
                const count = item.countKey ? counts?.[item.countKey] : undefined;
                return (
                  <button type="button" key={item.action} onClick={() => onAction(item.action)}>
                    <span className="kpx-account-menu__icon">{accountIcon(item.action)}</span>
                    <span className="kpx-account-menu__copy">
                      <strong>{item.label}</strong>
                      <small>{verification === null ? item.description : verification ? `${item.description} · 已提交（未核验）` : item.description}</small>
                    </span>
                    {count !== undefined ? <b>{compactNumber.format(count)}</b> : verification !== null ? <i className={verification ? "is-complete" : ""}>{verification ? "已提交" : "待提交"}</i> : null}
                    <ChevronRight size={18} />
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </nav>
      {showLogout ? <button className="kpx-account-hub__logout" type="button" onClick={() => onAction("logout")}>退出当前账号<ArrowRight size={16} /></button> : null}
      <footer className="kpx-account-hub__footer"><LockKeyhole size={14} />KIRA 会保护你的账号信息</footer>
    </aside>
  );
}

export const KIRA_POLICY_ITEMS = [
  { id: "terms", title: "用户服务协议", description: "账号、服务使用与双方权利义务" },
  { id: "privacy", title: "隐私政策", description: "个人信息处理与用户权利说明" },
  { id: "minors", title: "未成年人个人信息保护规则", description: "未成年人信息的专项保护边界" },
  { id: "collection", title: "个人信息收集清单", description: "按功能列明可能使用的信息类别" },
  { id: "third-parties", title: "第三方信息共享清单", description: "合作方、使用目的与共享范围" },
  { id: "community", title: "社区自律公约", description: "同好表达、互动与内容治理约定" },
  { id: "campaign-funds", title: "应援企划资金规则", description: "应援资金相关的独立规则入口" },
] as const;

export type KiraPolicyId = typeof KIRA_POLICY_ITEMS[number]["id"];
export type AboutKiraAction = KiraPolicyId | "contact" | "qualifications";

export interface AboutKiraProps {
  onAction: (action: AboutKiraAction) => void;
  onBack?: () => void;
  version?: string;
  contactLabel?: string;
  qualificationLabel?: string;
}

function policyIcon(id: KiraPolicyId): ReactNode {
  if (id === "terms") return <FileCheck2 size={19} />;
  if (id === "privacy") return <FileLock2 size={19} />;
  if (id === "minors") return <FileHeart size={19} />;
  if (id === "collection") return <FileText size={19} />;
  if (id === "third-parties") return <FileKey2 size={19} />;
  if (id === "community") return <UsersRound size={19} />;
  return <FileBadge size={19} />;
}

export function AboutKira({
  onAction,
  onBack,
  version = "KIRA 1.0 · 2026",
  contactLabel = "联系我们",
  qualificationLabel = "资质与备案信息",
}: AboutKiraProps) {
  const titleId = useId();
  return (
    <section className="kpx-about" aria-labelledby={titleId}>
      <header className="kpx-about__hero">
        <div className="kpx-about__topline">
          {onBack ? <button type="button" onClick={onBack} aria-label="返回账号中心"><ChevronLeft size={20} /></button> : <span />}
          <small>关于 KIRA</small>
          <span>{version}</span>
        </div>
        <h2 id={titleId}>关于 KIRA</h2>
        <p>和喜欢的人聊天，也和同好分享每个值得记住的瞬间。</p>
      </header>
      <ol className="kpx-policy-list">
        {KIRA_POLICY_ITEMS.map((policy) => (
          <li key={policy.id}>
            <button type="button" onClick={() => onAction(policy.id)}>
              <span className="kpx-policy-list__icon">{policyIcon(policy.id)}</span>
              <span><strong>{policy.title}</strong><small>{policy.description}</small></span>
              <ChevronRight size={18} />
            </button>
          </li>
        ))}
      </ol>
      <div className="kpx-about__utility">
        <button type="button" onClick={() => onAction("contact")}><MailQuestion size={20} /><span><strong>{contactLabel}</strong><small>服务时间与反馈渠道</small></span><ChevronRight size={18} /></button>
        <button type="button" onClick={() => onAction("qualifications")}><FileBadge size={20} /><span><strong>{qualificationLabel}</strong><small>主体信息、许可与备案</small></span><ChevronRight size={18} /></button>
      </div>
      <footer className="kpx-about__footer">
        <span><Star size={14} />感谢你使用 KIRA</span>
        <small>© 2026 KIRA</small>
      </footer>
    </section>
  );
}
